<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { useTheme } from "../composables/useTheme";

interface Properties {
  description?: string;
  isOpen: boolean;
  size?: "extra-large" | "fullscreen" | "large" | "medium" | "small";
  title: string;
}

const properties = withDefaults(defineProps<Properties>(), {
  description: "",
  size: "medium",
});

const emit = defineEmits<{
  close: [];
}>();

const { theme } = useTheme();

const fullscreenElement = ref<Element | null>(null);
const teleportTarget = computed<Element | string>(() => fullscreenElement.value ?? "body");
const mousedownTarget = ref<EventTarget | null>(null);

const sizeClass = computed(() => {
  if (properties.size === "small") {
    return "max-w-md";
  }
  if (properties.size === "large") {
    return "max-w-4xl";
  }
  if (properties.size === "extra-large") {
    return "max-w-6xl";
  }
  if (properties.size === "fullscreen") {
    return "max-w-none";
  }
  return "max-w-2xl";
});

function _handleFullscreenChange() {
  fullscreenElement.value = document.fullscreenElement;
}

function handleBackdropMousedown(event: MouseEvent) {
  mousedownTarget.value = event.target;
}

function handleBackdropClick(event: MouseEvent) {
  if (event.target === event.currentTarget && mousedownTarget.value === event.currentTarget) {
    emit("close");
  }
  mousedownTarget.value = null;
}

function _setBodyScrollLock(locked: boolean) {
  if (typeof document === "undefined") {
    return;
  }
  document.body.style.overflow = locked ? "hidden" : "";
}

watch(
  () => properties.isOpen,
  (open) => _setBodyScrollLock(open),
  {
    immediate: true,
  },
);

onMounted(() => {
  fullscreenElement.value = document.fullscreenElement;
  document.addEventListener("fullscreenchange", _handleFullscreenChange);
});

onBeforeUnmount(() => {
  document.removeEventListener("fullscreenchange", _handleFullscreenChange);
  _setBodyScrollLock(false);
});
</script>

<template>
  <Teleport :to="teleportTarget">
    <Transition enter-active-class="transition-opacity duration-150" enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-100" leave-to-class="opacity-0">
      <div v-if="isOpen" :data-theme="theme" :class="[
        'fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm',
        size === 'fullscreen' ? 'py-4' : 'py-12',
      ]"
        role="dialog" aria-modal="true" @mousedown="handleBackdropMousedown" @click="handleBackdropClick">
        <div :class="[
          'w-full rounded-2xl bg-app-bg p-7 ring-1 ring-app-border-strong shadow-app-large backdrop-blur-xl',
          sizeClass,
        ]">
          <h2 class="text-lg font-semibold text-app-text">{{ title }}</h2>
          <p v-if="description" class="mt-1 text-sm text-app-text-muted">
            {{ description }}
          </p>

          <div class="mt-5">
            <slot />
          </div>

          <div v-if="$slots.banner" class="mt-6">
            <slot name="banner" />
          </div>

          <div v-if="$slots.actions" class="mt-4 flex justify-end gap-3">
            <slot name="actions" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
